import type { NavPath } from '../utils/navigation';

// Every explanation behind a small (i), in one file, in plain words. One entry is read by the tip on
// the page and, later, by the Guide, so there is a single copy of each.
//
// Writing rules (glossary.test.ts checks the mechanical ones):
//   - say what it is, why it matters and what to do, in a sentence or two (about 35 words, never more than 45);
//   - coach-like: "needs a fix", "outside the usual owner", never "violation";
//   - no number written into the words: a limit an admin can change must not be contradicted. Say
//     "set in Settings → Standards", or put {name} where the page already holds the live value and
//     pass it as `vars` to the tip;
//   - no "simply", "just", "easy".

export interface GlossaryEntry {
  /** What the (i) is called, in the words on the page: "Rework". */
  term: string;
  /** What it means, in plain words. */
  short: string;
  /** A concrete case, when it makes the meaning clearer. */
  example?: string;
  /** Set on the "About this page" entry of a page: the page header shows it beside the title. */
  page?: NavPath;
}

const entry = (e: GlossaryEntry): GlossaryEntry => e;

export const GLOSSARY = {
  // ── Flow ──────────────────────────────────────────────────────────────────────
  'flow.page': entry({
    term: 'Flow',
    page: '/flow',
    short: 'How tickets move through Jira: who moves them, which ones come back, and where they wait too long. It points at slow or messy handovers so they can be fixed, not at people.',
  }),
  'flow.handoffs': entry({
    term: 'Outside the usual owner',
    short: 'Each stage has a usual owner, such as QA for testing. A ticket moved into a stage by someone else is listed here (often called a handoff). It is a nudge to hand over with a comment, not a mistake.',
  }),
  'flow.adherence': entry({
    term: 'By the usual owner',
    short: 'The share of moves made by the person whose role owns that stage. Moves into shared stages, and moves by people with no role set, are not counted either way.',
  }),
  'flow.rework': entry({
    term: 'Sent back',
    short: 'Tickets that went backwards (often called rework): reopened, sent back for changes, or moved to an earlier stage. A few are normal. Many in one place often means the ticket was unclear at the start.',
    example: 'A ticket moves from In QA back to In Progress.',
  }),
  'flow.waiting': entry({
    term: 'Waiting',
    short: 'How long tickets stay in each status, and which open tickets have sat longer than that status allows. Long waits usually mean a ticket is blocked or has been forgotten. Open to everyone.',
  }),
  'flow.stuck': entry({
    term: 'Not moved for a while',
    short: 'An open ticket that has stayed in one status longer than the limit for that status (often called stuck). The limits are set per status in Settings → Flow. Tickets that have not moved for very long are often finished but never closed in Jira.',
  }),
  'flow.slowest': entry({
    term: 'Slowest stage',
    short: 'The stage where tickets typically waited longest this month. The line under it shows how long the slowest tenth of tickets waited there.',
  }),
  'flow.time-in-status': entry({
    term: 'Time in each status',
    short: 'For each status, the typical wait (the middle value, so one very slow ticket does not skew it) for tickets that left it this month, plus how long the slowest tenth waited.',
  }),

  // ── Time logging ──────────────────────────────────────────────────────────────
  'effort.page': entry({
    term: 'Time logging',
    page: '/effort',
    short: 'Checks that each time entry starts with one standard tag in square brackets, such as [Dev], and lists tickets in progress with nothing logged. Tags make hours comparable across teams.',
  }),
  'effort.logging': entry({
    term: 'Entries to fix',
    short: "This month's time entries that need a fix: no tag, a tag that is not on the list, or two tags. Fix the entry in Jira and it drops off this list shortly after.",
  }),
  'effort.gaps': entry({
    term: 'No time on open work',
    short: 'Where time is missing (often called gaps): open tickets that have been in progress for a while with nothing logged, and people who logged regularly and then went quiet. Open to everyone.',
  }),
  'effort.standard-tag': entry({
    term: 'Standard tag',
    short: 'The word in square brackets that starts a time entry, such as [Dev] or [Testing]. An entry is valid with exactly one tag from the list. Admins keep the list in Settings → Standards.',
    example: '[Dev] Fixed the date filter',
  }),
  'effort.valid-share': entry({
    term: 'Entries with one standard tag',
    short: "The share of this month's time entries that start with exactly one tag from the list. Higher is better. Months before the tagging standard began are not judged.",
  }),
  'effort.trend': entry({
    term: 'Month by month',
    short: 'The same share for recent months, so you can see whether logging is getting tidier. This month is the dark bar.',
  }),
  'effort.no-time': entry({
    term: 'In progress, nothing logged',
    short: 'Tickets that have been in progress for several days with no time on them or their sub-tasks. Worth asking about in standup. The number of days is set in Settings → Standards.',
  }),
  'effort.quiet': entry({
    term: 'Went quiet',
    short: 'People who logged time regularly and then nothing for a while. It is a prompt to check in; they may be on leave. The length of the gap is set in Settings → Standards.',
  }),
  // ── Plan ──────────────────────────────────────────────────────────────────
  'plan.page': entry({
    term: 'Plan',
    page: '/plan',
    short: "The quarter's plan, project by project, as the planning sheet showed it. The status of each item is read from Jira, so nothing is entered twice. Anyone can add an idea; leaders, admins and the project owner change the rest.",
  }),
  'plan.status': entry({
    term: 'Plan status',
    short: 'A plain reading of the Jira status, so the plan adds no statuses of its own: Idea, Picked, In progress, On hold, Live or Dropped. The Jira status itself is shown beside it.',
  }),
  'plan.idea': entry({
    term: 'Idea',
    short: 'A thing someone wants that has no Jira ticket yet. It waits in the plan. When the ticket is made, attach it by its key: the notes move to the ticket and the idea leaves the list. Jira stays the only place work is recorded.',
  }),
  'plan.priority': entry({
    term: 'Priority',
    short: 'How soon the team wants it, from the first rank to the last. Jira has no filled field for it, so the plan keeps it. A leader, an admin or the project owner sets it.',
  }),
  'plan.adhoc': entry({
    term: 'Ad hoc',
    short: "The quarter's unplanned asks: the tickets inside each project's intake epic, as they stand in Jira. They are not goals, so they are listed apart from the plan.",
  }),
  'plan.ideation': entry({
    term: 'Ideation',
    short: "Asks that came from outside the teams, such as the council and directors, kept as ideas until they are picked up. Each shows who asked.",
  }),
  'plan.roadmap': entry({
    term: 'Roadmap',
    short: "The quarter's goals across its sprints. A cell counts the goal's tickets that are in that sprint now, and how many of them are done. A ticket carried over shows in every sprint it has been in.",
  }),
  'plan.function': entry({
    term: 'By function',
    short: 'Which of the five functions is on each goal: Developers, QA, DevOps, Product and Design. A ticket belongs to the function of the person it is assigned to; with nobody on it, to the function that does that kind of ticket.',
  }),
  'plan.intake': entry({
    term: 'Intake',
    short: "What came in during a month through each project's intake epic, where each ask stands, and the older asks still open. It shows how much unplanned work arrives, and how fast it is picked up.",
  }),
  'plan.before': entry({
    term: 'Before the Jira conventions',
    short: 'Roadmap items that finished before the Jira conventions started. They are kept apart so they are never mixed into the plan or compared with later work. The start day is set in Settings → Standards.',
  }),
  // ── Report ────────────────────────────────────────────────────────────────
  'report.page': entry({
    term: 'Report',
    page: '/report',
    short: 'The mid-month and monthly report, made from Jira, for the leader to send. Everything in it is read from Jira; only a line\'s remark and the roadblocks are written by hand. Download it as Word or Markdown, and keep a copy of what you send.',
  }),
  'report.timeline': entry({
    term: 'The dates in Status',
    short: 'The first day any ticket inside the work reached each step, such as in design, in development, on staging, in QA and on prod. An epic itself moves once, so its steps come from its tickets. Bugs are left out of the story.',
  }),
  'report.kept': entry({
    term: 'Kept copies',
    short: 'A copy of exactly what was sent. The page above stays live and changes as Jira does; a kept copy never changes. Only an admin can remove one.',
  }),
  // ── My people ─────────────────────────────────────────────────────────────
  'people.page': entry({
    term: 'My people',
    page: '/my-people',
    short: 'The people in the teams you manage or are on, with the month so far: hours logged, tickets shipped, open tickets in a running sprint and what is waiting. It is a way to see who may need a hand, not a ranking.',
  }),
  // ── Planning ──────────────────────────────────────────────────────────────
  'planning.page': entry({
    term: 'Planning',
    page: '/planning',
    short: "How far each quarter's goals have come, and how each sprint went, so slipping work shows up while there is still time to act. Open to everyone.",
  }),
  'planning.sprint': entry({
    term: 'Sprint',
    short: "How a sprint went, as Jira's Sprint Report shows it: what was committed, added, removed and completed, and which tickets keep being carried over.",
  }),
  'planning.goal-epics': entry({
    term: 'Quarter goals',
    short: "The epics named for the quarter, with the quarter in square brackets at the front of the name. A quarter's progress is the progress of the tickets inside these goals.",
  }),
  'planning.shipped-ready': entry({
    term: 'Shipped or release-ready',
    short: 'A ticket counts as progress once it is shipped, or finished and waiting for the next release. Tickets closed as invalid are left out of the count.',
  }),
  'planning.checkpoint': entry({
    term: 'Halfway check',
    short: 'Taken at the end of the sprint that closes the middle of the quarter. It compares the share of tickets shipped or release-ready by then with a target set in Settings → Standards.',
  }),
  'planning.scope-creep': entry({
    term: 'Added after the start',
    short: 'Work added to a sprint after it started, as a share of what was committed at the start (often called scope creep). Some is normal. Above the limit set in Settings → Standards usually means planning was not finished before the sprint began.',
  }),
  'planning.rollover': entry({
    term: 'Carried over',
    short: "A ticket is carried over when a sprint ends before it is done and it moves to a later sprint (Jira calls this a rollover). Jira counts the times and records the reason. Tickets carried over again and again need a lead's review.",
  }),
  'planning.tech_from': entry({
    term: 'Before the Jira conventions',
    short: 'Months before {from} sit apart. The Jira conventions (names, workflow, planning, time logging) were not followed yet, so their numbers are shown but never compared with later months. The start day is set in Settings → Standards.',
  }),
  // ── Naming ────────────────────────────────────────────────────────────────
  'naming.page': entry({
    term: 'Naming',
    page: '/hygiene',
    short: "Checks that a ticket's title tells QA or Product what changes without opening it. It flags titles that are vague, technical or written as done work; it never judges style or capital letters.",
  }),
  'naming.clear': entry({
    term: 'Clear',
    short: 'A title is clear when none of the naming checks found a problem. The checks are deliberately coarse, so a clear title is not necessarily a perfect one. Admins can change them in Settings → Naming rules.',
  }),
  'naming.attention': entry({
    term: 'Needs attention',
    short: 'Titles the naming checks flagged, such as one that does not say what will be done, is too short, or uses code words. Each shows a reason and a suggestion. Rename it in Jira and it drops off after the next sync.',
  }),
  // ── Compliance and My audit ───────────────────────────────────────────────
  'compliance.page': entry({
    term: 'Compliance',
    page: '/compliance',
    short: 'How each project is doing against the four standards this month. Each standard is one plain percentage set against last month; there is no combined score. Press a figure to open the page behind it.',
  }),
  'compliance.standards': entry({
    term: 'The four standards',
    short: 'Naming: tickets with a clear title. Flow: moves made by the usual owner. Time logging: time entries with one standard tag. Planning: goal tickets shipped or release-ready. Press one to open its page.',
  }),
  'compliance.change': entry({
    term: 'Change from last month',
    short: 'The arrow shows how many percentage points a standard moved since last month. A fall is shown in amber as a nudge to look, not as a failure. The month in progress is not final.',
  }),
  'audit.page': entry({
    term: 'My audit',
    page: '/my-audit',
    short: 'Your own items from the four standards: titles to improve, moves outside the usual owner, time entries to fix, and tickets that keep rolling over. Only your work is shown. Fix an item in Jira and it drops off.',
  }),
  // ── Home ──────────────────────────────────────────────────────────────────
  'home.waiting': entry({
    term: 'Waiting',
    short: "Open tickets that have not moved for longer than their status allows, the same list as Flow's waiting tab. Very old ones, usually shipped but never closed in Jira, are left out. Each status has its own limit, set in Settings → Flow.",
  }),
  'home.page': entry({
    term: 'Home',
    page: '/home',
    short: 'Where to start. A team member sees what to fix, their hours and their projects; a leader sees how the quarter is going; an admin sees the projects furthest from the standards. The search finds any ticket, person or project you can open.',
  }),
  // ── Dashboard ─────────────────────────────────────────────────────────────
  // ── Reports ───────────────────────────────────────────────────────────────
  'reports.page': entry({
    term: 'Epic reports',
    page: '/reports-overview',
    short: 'Every epic that was active in the month, quarter or year, with the status it had at the end of that period and how it got there. Narrow the list by status or category, or download it as Excel.',
  }),
  'reports.roster': entry({
    term: 'Which epics are listed',
    short: 'An epic is listed if it was created before the period ended, was not closed as Invalid, Dropped or Duplicate, and did not finish before the period and stay finished. So an unfinished epic keeps appearing until it is done.',
  }),
  'reports.by-category': entry({
    term: 'By category',
    short: "The same epics sorted by the kind of work, read from the epic's name: DevOps, Automation, Ad Hoc, or the rest. Each epic is in one group, so the groups add up to the total.",
  }),
  'reports.description': entry({
    term: 'Description',
    short: 'When an epic has no description of its own, or shares one with other epics, this shows what is inside it instead: tickets, how many are open, hours logged in the period and the latest ticket. It is counted, not written by a model.',
  }),
  'reports.history': entry({
    term: 'Status history',
    short: 'The steps an epic went through, oldest first. The last line is its status at the end of the period.',
  }),
  'worklogs.page': entry({
    term: 'Worklogs by person',
    page: '/reports',
    short: 'The hours each person logged on each day of the month. Press a day to see the tickets and entries behind it. You see your team and yourself; admins and leaders see everyone.',
  }),
  'worklogs.no-log': entry({
    term: 'Weekdays with no log',
    short: 'Weekdays that are over on which a person logged no time, counted for the people shown. It can include leave and public holidays, which the portal does not know about. Press it to show only those days.',
  }),
  'worklogs.tags': entry({
    term: 'Tags',
    short: "How the month's logged hours split across tags such as Dev, Testing or Design, for each team. Entries with no standard tag are counted as Untagged.",
  }),
  // ── Settings ──────────────────────────────────────────────────────────────
  'settings.page': entry({
    term: 'Settings',
    page: '/settings',
    short: 'Where an admin sets up the portal: the Jira connection, the rules and limits the standards pages judge by, and people and teams. Only admins can open it.',
  }),
  'settings.update-data': entry({
    term: 'Update data',
    short: 'Copies the latest users, issues, worklogs and status history from Jira into this portal. Run it when a page looks out of date; the table shows how recent syncs went.',
  }),
  'settings.jira-config': entry({
    term: 'Jira configuration',
    short: 'The address of your Jira site and the account the portal uses to read it. Change it only if the site or the account changed; a wrong value stops syncing.',
  }),
  'settings.field-mapping': entry({
    term: 'Field mapping',
    short: "Tells the portal which Jira custom field holds each planning detail, such as the rollover count and reasons. If Jira's fields are renamed or replaced, update the mapping here.",
  }),
  'settings.smtp-config': entry({
    term: 'SMTP / email',
    short: 'The mail server and sender details the portal uses to send email. Change them only if email stops arriving.',
  }),
  'settings.naming-rules': entry({
    term: 'Naming rules',
    short: 'The checks behind the Naming page, and the list of action verbs a good title can start with. Each rule can be switched off or reworded. A change applies to every month, past included.',
  }),
  'settings.flow': entry({
    term: 'Flow',
    short: 'Who owns each stage of work, how long a ticket may sit in a stage before it counts as stuck, and which role each person has. These drive the Flow page. A change applies to every month, past included.',
  }),
  'settings.standards': entry({
    term: 'Standards',
    short: 'The limits that Planning and Time logging judge by, and the list of standard worklog tags. Every change is written to the change log. A change applies to every month, past included; Reset puts the built-in value back.',
  }),
  'settings.create-users': entry({
    term: 'Create users',
    short: "Create portal logins, import people from Jira, and set each login's role: admin, leader or user. A login is linked to its Jira person so My audit and the checks know whose work is whose.",
  }),
  'settings.manage-teams': entry({
    term: 'Manage teams',
    short: "Group people into teams and attach projects. A user sees their team's worklogs and checks; a user with no team sees only their own.",
  }),
  'settings.kudos': entry({
    term: 'Kudos',
    short: 'Every kudos that leaders and admins have sent, newest first. Take one back if it went to the wrong person or the note should not stay. Nothing here is counted or ranked, and the giver is not told.',
  }),
  'settings.plan-import': entry({
    term: 'Plan import',
    short: "Bring the planning sheet's rows into the Plan. Check first to see what would happen, then import. A row with a Jira ticket fills that ticket's notes; a row without one becomes an idea. Jira always wins, and nothing in Jira is changed.",
  }),
  'settings.projects': entry({
    term: 'Projects',
    short: 'Choose who owns each project and who is involved. They see the project and its updates on their Home. This does not give anyone more access to other pages. The teams on a project are set under Manage teams.',
  }),
};

export type GlossaryId = keyof typeof GLOSSARY;

/** Puts live values into a tip: "{days}" becomes the number the page holds. A name with no value is left as written. */
export function fillTip(text: string, vars?: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (vars && name in vars ? String(vars[name]) : whole));
}

// The page each family of words belongs to (the part of an id before the dot), for the Guide.
export const PAGE_OF_TERM: Record<string, NavPath> = {
  home: '/home',
  reports: '/reports-overview',
  worklogs: '/reports',
  compliance: '/compliance',
  audit: '/my-audit',
  naming: '/hygiene',
  flow: '/flow',
  effort: '/effort',
  planning: '/planning',
  plan: '/plan',
  report: '/report',
  people: '/my-people',
  settings: '/settings',
};

export const pageOfTerm = (id: GlossaryId): NavPath | undefined => PAGE_OF_TERM[id.split('.')[0]];

/** The "About this page" tip of a page, if it has one. */
export function pageTipFor(pathname: string): GlossaryId | undefined {
  return (Object.keys(GLOSSARY) as GlossaryId[]).find((id) => (GLOSSARY[id] as GlossaryEntry).page === pathname);
}
